import { useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { formatMatchClock } from '@/types';
import type { LiveSession } from '@/types';
import LiveMatchHeader from '@/components/live/LiveMatchHeader';
import {
  LIVE_PERIOD_LABELS,
  LIVE_PERIODS,
  LIVE_STATUS_LABELS,
  OT_FLOW_HINT,
  formatClockInput,
  getPeriodCapSeconds,
  isClockRunning,
  isPeriodSegmentComplete,
  parseClockInput,
  type LivePeriod,
} from '@/utils/liveClock';

interface Props {
  session: LiveSession;
  currentSeconds: number;
  onClockAction: (action: string, extra?: { adjust_seconds?: number; period?: string; seconds?: number }) => void;
  disabled?: boolean;
  compact?: boolean;
  inverted?: boolean;
  eventCount?: number;
  showMatchHeader?: boolean;
}

export default function LiveClockPanel({
  session,
  currentSeconds,
  onClockAction,
  disabled,
  compact = false,
  inverted = false,
  eventCount,
  showMatchHeader,
}: Props) {
  const theme = useTheme();
  const overlayZIndex = inverted ? theme.zIndex.modal + 25 : undefined;
  /** Above LiveRecordingMode overlay (modal+10); must be on Menu root, not Paper only. */
  const periodMenuZIndex = inverted ? 1500 : undefined;
  const periodMenuProps =
    periodMenuZIndex != null
      ? {
          disablePortal: true,
          disableScrollLock: true,
          sx: { zIndex: periodMenuZIndex },
          PaperProps: {
            sx: {
              zIndex: periodMenuZIndex,
              maxHeight: 320,
              bgcolor: '#1e2630',
              color: '#fff',
              '& .MuiMenuItem-root': {
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                '&.Mui-selected': { bgcolor: 'rgba(139,195,74,0.24)' },
                '&.Mui-selected:hover': { bgcolor: 'rgba(139,195,74,0.32)' },
              },
            },
          },
        }
      : undefined;
  const [editOpen, setEditOpen] = useState(false);
  const [editValue, setEditValue] = useState('0:00');

  const isEnded = session.status === 'ended';
  const running = isClockRunning(session);
  const matchMinutes = session.match_duration_minutes ?? 30;
  const periodCap = getPeriodCapSeconds(session.current_period, matchMinutes);
  const periodSegmentComplete = isPeriodSegmentComplete(
    session.current_period,
    matchMinutes,
    currentSeconds,
    running
  );
  const periodLabel =
    LIVE_PERIOD_LABELS[session.current_period as LivePeriod] || session.current_period;
  const statusLabel = LIVE_STATUS_LABELS[session.status] || session.status;

  const openEdit = () => {
    setEditValue(formatClockInput(currentSeconds));
    setEditOpen(true);
  };

  const saveEdit = () => {
    const parsed = parseClockInput(editValue);
    if (parsed == null) return;
    onClockAction('set_time', { seconds: parsed });
    setEditOpen(false);
  };

  const handleEnd = () => {
    if (!window.confirm('確定結束整場比賽？結束後將無法再記錄事件。')) return;
    onClockAction('end');
  };

  const handleStartOrResume = () => {
    if (periodSegmentComplete) return;
    onClockAction(session.status === 'paused' || session.status === 'halftime' ? 'resume' : 'start');
  };

  const periodSelectSx = inverted
    ? {
        color: '#fff',
        bgcolor: 'rgba(139,195,74,0.32)',
        borderRadius: 999,
        fontWeight: 800,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(139,195,74,0.75)' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(139,195,74,0.95)' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'secondary.main' },
        '& .MuiSvgIcon-root': { color: '#fff' },
        '&.Mui-disabled': {
          color: 'rgba(255,255,255,0.45)',
          bgcolor: 'rgba(255,255,255,0.08)',
        },
      }
    : {
        borderRadius: 999,
        fontWeight: 800,
        bgcolor: 'secondary.main',
        color: 'secondary.contrastText',
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
        '& .MuiSvgIcon-root': { color: 'inherit' },
      };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: compact ? 0.75 : 2,
        color: inverted ? '#fff' : 'inherit',
        width: '100%',
      }}
    >
      <Stack
        spacing={compact ? 0.75 : 1.25}
        alignItems="center"
        sx={{ width: '100%', maxWidth: inverted ? 560 : compact ? 320 : 420, mx: 'auto' }}
      >
        {(showMatchHeader ?? false) && (
          <LiveMatchHeader
            session={session}
            compact={compact}
            inverted={inverted}
            eventCount={eventCount}
          />
        )}

        <FormControl
          size={compact ? 'small' : 'medium'}
          sx={{
            width: 'fit-content',
            minWidth: compact ? 132 : 160,
            ...(inverted ? { position: 'relative', zIndex: 3 } : {}),
          }}
        >
          <Select
            value={session.current_period}
            onChange={(e) => onClockAction('set_period', { period: e.target.value })}
            disabled={disabled || isEnded}
            displayEmpty
            IconComponent={KeyboardArrowDownIcon}
            sx={{
              ...periodSelectSx,
              '& .MuiSelect-select': {
                py: compact ? 0.65 : 0.85,
                px: 2,
                textAlign: 'center',
              },
            }}
            MenuProps={periodMenuProps}
          >
            {LIVE_PERIODS.map((p) => (
              <MenuItem key={p} value={p} sx={{ justifyContent: 'center' }}>
                {LIVE_PERIOD_LABELS[p]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box
          sx={{
            position: 'relative',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: compact ? 48 : 56,
            px: 5,
          }}
        >
          <Typography
            variant={compact ? 'h3' : 'h2'}
            sx={{
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: compact ? 1 : 2,
              lineHeight: 1.1,
              textAlign: 'center',
            }}
          >
            {formatMatchClock(currentSeconds)}
          </Typography>
          {!isEnded && (
            <Tooltip title="編輯時間">
              <span>
                <IconButton
                  size="small"
                  onClick={openEdit}
                  disabled={disabled || running}
                  aria-label="編輯時間"
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: inverted ? 'rgba(255,255,255,0.9)' : undefined,
                    border: inverted ? '1px solid rgba(255,255,255,0.25)' : undefined,
                    bgcolor: inverted ? 'rgba(255,255,255,0.08)' : undefined,
                    '&:hover': inverted
                      ? { bgcolor: 'rgba(255,255,255,0.16)' }
                      : undefined,
                    '&.Mui-disabled': {
                      color: inverted ? 'rgba(255,255,255,0.35)' : undefined,
                      borderColor: inverted ? 'rgba(255,255,255,0.12)' : undefined,
                    },
                  }}
                >
                  <EditIcon fontSize="small" sx={{ color: 'inherit' }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>

        <Typography
          variant="caption"
          color={inverted ? 'rgba(255,255,255,0.65)' : 'text.secondary'}
          sx={{ textAlign: 'center', width: '100%' }}
        >
          狀態：{statusLabel}
          {periodCap != null && ` · 本段上限 ${formatMatchClock(periodCap)}`}
          {` · 全場 ${matchMinutes} 分鐘`}
        </Typography>

        {!compact && (
          <Typography
            variant="caption"
            color={inverted ? 'rgba(255,255,255,0.55)' : 'text.secondary'}
            sx={{ textAlign: 'center', width: '100%', px: 0.5 }}
          >
            {OT_FLOW_HINT}
          </Typography>
        )}

        {!isEnded && (
          <Stack spacing={compact ? 1 : 1.5} alignItems="center" sx={{ width: '100%' }}>
            <ButtonGroup variant="contained" size={compact ? 'small' : 'medium'} disabled={disabled}>
              {!running ? (
                <Button
                  startIcon={<PlayArrowIcon />}
                  onClick={handleStartOrResume}
                  disabled={periodSegmentComplete}
                >
                  {session.status === 'paused' || session.status === 'halftime' ? '繼續' : '開始'}
                </Button>
              ) : (
                <Button startIcon={<PauseIcon />} onClick={() => onClockAction('pause')}>
                  暫停
                </Button>
              )}
              <Button startIcon={<StopIcon />} color="error" onClick={handleEnd}>
                結束
              </Button>
            </ButtonGroup>

            {periodSegmentComplete && (
              <Typography variant="caption" color="warning.main" sx={{ textAlign: 'center', width: '100%' }}>
                本節已結束，請從上方選單切換至下一節次（例如{periodLabel === '上半場' ? '下半場' : '下一節'}）後再開始
              </Typography>
            )}

            <ButtonGroup size="small" disabled={disabled}>
              <Button onClick={() => onClockAction('adjust', { adjust_seconds: -10 })}>-10s</Button>
              <Button onClick={() => onClockAction('adjust', { adjust_seconds: -1 })}>-1s</Button>
              <Button onClick={() => onClockAction('adjust', { adjust_seconds: 1 })}>+1s</Button>
              <Button onClick={() => onClockAction('adjust', { adjust_seconds: 10 })}>+10s</Button>
            </ButtonGroup>
          </Stack>
        )}
      </Stack>

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        fullWidth
        maxWidth="xs"
        sx={overlayZIndex != null ? { zIndex: 1500 } : undefined}
      >
        <DialogTitle>編輯比賽時間</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="時間 (m:ss)"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            fullWidth
            placeholder="例：15:30"
            helperText="請在計時暫停或待開始時調整"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>取消</Button>
          <Button variant="contained" color="secondary" onClick={saveEdit}>
            儲存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
